import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, FileText, User, Eye, CheckCircle, DollarSign, MapPin, Star, ThumbsDown, Undo2, Settings, Briefcase, LayoutDashboard, Clock, MessageSquare, Users } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiPost, apiBlob } from '@/lib/api';
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout';
import { PageHeader, SectionCard, SummaryStatCard, EmptyState, StatusBadge, BackLink, ReadOnlyValue, FilterToolbar } from '@/components/shared/DashboardUI';
import ProfileTab from '@/components/shared/ProfileTab';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { formatDate, formatDateOnly, formatRelativeTime, getCaseTypeLabel, getInsuranceCarrierLabel, getLiabilityLimitsLabel, US_STATES, formatTreatmentLocations, formatBandPeriodLabel, formatBandDateRangeLabel } from '@/lib/formatters';
// Vocabularies and defaults come from the shared pricing engine, never from
// literals typed here: the backend validates against the same lists, and a
// second copy is how a select silently offers a value the API 400s on.
import {
  DEFAULT_FEE_MODEL, DEFAULT_INTERVAL, DEFAULT_PROCESSING_FEE,
  PRICING_VERSION, VALID_FEE_MODELS, VALID_INTERVALS,
} from '@/lib/offerPricing';
import FunderOfferBuilder from '@/components/FunderOfferBuilder';
import FunderPortfolioDashboard from '@/components/FunderPortfolioDashboard';
import { passesFunderSettings, countActionable, isWinnable, hasLiveOffer } from '@/lib/funderAvailability';
import MessagesInbox from '@/components/shared/MessagesInbox';
import AgreementModal from '@/components/AgreementModal';
import { LawyerSubscriptions } from '@/components/LawyerSubscriptions';
import { DocumentUpload } from '@/components/DocumentUpload';
import DocumentRequestsCard from '@/components/DocumentRequestsCard';
import MessagesPanel from '@/components/shared/MessagesPanel';
import FunderFinalContractCard from '@/components/FunderFinalContractCard';
import { ActivityTimelineCard } from '@/components/ActivityTimeline';
import '@/styles/authenticated.css';


// Built per render so Browse can carry the count of applications still
// waiting for an offer. A falsy badge renders nothing, so zero is invisible
// rather than a "0" pill advertising that there is no work.
const navItems = (availableCount) => [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'browse', label: 'Browse Applications', icon: Search, badge: availableCount || undefined },
  { id: 'my-leads', label: 'My Leads', icon: Star },
  { id: 'my-loans', label: 'My Loans', icon: Briefcase },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'profile', label: 'Profile', icon: User },
];

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/* ================================================================
   Offer Activity — what the marketplace has done with an application
   ================================================================ */
/**
 * Reads "Other offers in · none accepted", never a number. An exact count is a
 * price signal: it tells a funder how hard to undercut, and a high one deters
 * as readily as it attracts.
 *
 * Whether THIS funder has bid is deliberately absent — the action button says
 * it ("Update offer" rather than "Make an offer"), and a badge here said it a
 * second time in the same row.
 *
 * Both states therefore say "other", and the quiet one is not negotiable copy:
 * as a bare "No offers yet" it read as an absolute, so a funder with an offer
 * of their own saw a row contradict itself — "No offers yet" beside "Update
 * offer".
 */
const OfferActivity = ({ application }) => {
  const contested = application.has_other_offers;
  return (
    <div data-testid={`offer-activity-${application.application_id}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-start' }}>
      <span
        className={`mcl-badge ${contested ? 'mcl-badge-warning' : 'mcl-badge-secondary'}`}
        style={{ fontSize: '0.75rem' }}
      >
        {contested ? <><Users size={11} style={{ marginRight: '4px' }} /> Other offers in &middot; none accepted</> : 'No other offers yet'}
      </span>
      {application.validated_at && (
        <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>
          approved {formatRelativeTime(application.validated_at)}
        </span>
      )}
    </div>
  );
};

/**
 * A promoted application: others have bid, the plaintiff has chosen none of
 * them, and this funder has not bid. Hoisted so React does not recreate the
 * component type on every render.
 */
const WinnableCard = ({ application, onView, onMakeOffer }) => (
  <div className="mcl-card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid={`winnable-card-${application.application_id}`}>
    <div style={{ padding: '1.1rem 1.15rem 1.15rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flexGrow: 1 }}>
      <div>
        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--mcl-text-heading)', lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
          {application.requestedAmount ? `$${parseFloat(application.requestedAmount).toLocaleString()}` : '-'}
        </div>
        <div style={{ fontWeight: 700, color: 'var(--mcl-text-heading)', fontSize: '0.9375rem', marginTop: '0.45rem' }}>
          {application.firstName} {application.lastName}
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--mcl-text-muted)', marginTop: '0.1rem' }}>
          {getCaseTypeLabel(application.caseType)} &middot; {application.accidentState || application.state || '-'}
        </div>
      </div>
      <span className="mcl-badge mcl-badge-warning" style={{ fontSize: '0.75rem', alignSelf: 'flex-start' }}>
        <Users size={11} style={{ marginRight: '4px' }} /> Other offers in &middot; none accepted
      </span>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 'auto' }}>
        <button className="mcl-btn mcl-btn-sm mcl-btn-primary" onClick={() => onMakeOffer(application)} data-testid={`card-make-offer-${application.application_id}`}>
          <DollarSign size={14} /> Make an offer
        </button>
        <button className="mcl-action-btn" onClick={() => onView(application.application_id)} data-testid={`card-view-${application.application_id}`}>
          <Eye size={14} /> View
        </button>
      </div>
    </div>
    <div style={{ height: '3px', background: 'var(--mcl-warning)' }} />
  </div>
);

/* ================================================================
   Funder Interaction Badge
   ================================================================ */
const InteractionBadge = ({ favorited, notInterested, hasOffer, offerStatus }) => {
  if (hasOffer) {
    const configs = {
      draft: { label: 'Draft Offer', cls: 'mcl-badge-muted' },
      submitted: { label: 'Offer Submitted', cls: 'mcl-badge-info' },
      accepted: { label: 'Offer Accepted', cls: 'mcl-badge-success' },
      declined: { label: 'Offer Declined', cls: 'mcl-badge-danger' },
      rejected: { label: 'Offer Rejected', cls: 'mcl-badge-danger' },
      withdrawn: { label: 'Offer Withdrawn', cls: 'mcl-badge-muted' },
    };
    const cfg = configs[offerStatus] || configs.draft;
    return <span className={`mcl-badge ${cfg.cls}`} style={{ fontSize: '0.7rem' }}><DollarSign size={10} style={{ marginRight: '3px' }} /> {cfg.label}</span>;
  }
  if (favorited) return <span className="mcl-badge mcl-badge-warning" style={{ fontSize: '0.7rem' }}><Star size={10} style={{ marginRight: '3px' }} /> Favorited</span>;
  if (notInterested) return <span className="mcl-badge mcl-badge-muted" style={{ fontSize: '0.7rem' }}><ThumbsDown size={10} style={{ marginRight: '3px' }} /> Not Interested</span>;
  return null;
};

// Tabs that own a URL segment. 'dashboard' is the bare /funder route.
const FUNDER_TABS = ['browse', 'my-leads', 'my-loans', 'messages', 'settings', 'profile'];

const DEFAULT_SETTINGS = { max_loan_amount: null, max_cap_rate: 2.5, max_annualized_rate: 28, max_term_months: 48, preferred_states: [] };

const FunderDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [funderSettings, setFunderSettings] = useState(DEFAULT_SETTINGS);
  const [needsAgreement, setNeedsAgreement] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  // The marketplace list lives here rather than inside BrowseTab because three
  // things outside Browse read a count off it: the sidebar badge, the dashboard
  // banner and the pipeline bar. Two copies of the list would drift the moment
  // a funder favorited or bid on something.
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts[2] === 'application' && pathParts[3]) { setSelectedApplicationId(pathParts[3]); setActiveTab('browse'); }
    // Tabs own a URL so they can be bookmarked, shared and reloaded — the same
    // pattern LawyerDashboard already uses. The whitelist keeps an unknown
    // segment from selecting a tab that does not exist.
    else if (pathParts[2] && FUNDER_TABS.includes(pathParts[2])) {
      setActiveTab(pathParts[2]); setSelectedApplicationId(null);
    }
    else { setSelectedApplicationId(null); }
  }, [location.pathname]);

  useEffect(() => { fetchUser(); fetchSettings(); checkAgreement(); fetchApplications(); }, []);

  const fetchUser = async () => {
    try { setUser(await apiGet('/users/me')); } catch (error) { console.error('Failed:', error); }
  };

  const fetchSettings = async () => {
    try { setFunderSettings(await apiGet('/funder/settings')); } catch (error) { console.error('Failed to load settings:', error); }
  };

  const fetchApplications = async () => {
    try { setApplications(await apiGet('/applications/available')); } catch (error) { console.error('Failed to load applications:', error); }
  };

  const checkAgreement = async () => {
    try {
      const data = await apiGet('/agreements/status');
      setNeedsAgreement(data.needs_signature);
    } catch (error) { console.error('Failed to check agreement:', error); }
    finally { setAgreementChecked(true); }
  };

  const availableCount = countActionable(applications, funderSettings);

  const handleNavClick = (tab) => { setActiveTab(tab); setSelectedApplicationId(null); navigate(tab === 'dashboard' ? '/funder' : `/funder/${tab}`); };
  const handleViewApplication = (appId) => { navigate(`/funder/application/${appId}`); };
  const handleBackToList = () => { setSelectedApplicationId(null); navigate('/funder'); };

  // Gate: Funder must be approved to access dashboard
  if (user && user.funder_approved === false) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '2rem 1rem' }}>
        <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }} data-testid="funder-pending-approval">
          <div style={{ background: '#fff', borderRadius: 12, padding: '3rem 2.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#fef3c7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <Clock size={36} style={{ color: '#d97706' }} />
            </div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>Account Pending Approval</h1>
            <p style={{ color: '#64748b', lineHeight: 1.7, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Thank you for registering with MyCaseLoan! Your funder account is currently under review. You will receive an email once your account has been approved by our team.
            </p>
            <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '1rem', marginBottom: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569' }}>
                <strong>What happens next?</strong><br />
                Our admin team will review your registration and approve your account. Once approved, you&apos;ll have full access to browse applications, submit offers, and manage your portfolio.
              </p>
            </div>
            <button className="mcl-btn mcl-btn-outline" onClick={() => { localStorage.removeItem('access_token'); navigate('/login'); }} data-testid="funder-pending-logout-btn">
              Back to Login
            </button>
          </div>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '1.5rem' }}>&copy; 2026 MyCaseLoan. All rights reserved.</p>
        </div>
      </div>
    );
  }

  const pageTitles = { dashboard: 'Dashboard', browse: 'Browse Applications', 'my-leads': 'My Leads', 'my-loans': 'My Loans', messages: 'Messages', settings: 'Settings', profile: 'Profile' };

  return (
    <>
      {agreementChecked && needsAgreement && (
        <AgreementModal onSigned={() => setNeedsAgreement(false)} />
      )}
      <AuthenticatedLayout user={user} activeTab={activeTab} onTabChange={handleNavClick} navItems={navItems(availableCount)} role="funder" pageTitle={pageTitles[activeTab]}>
      {activeTab === 'dashboard' && <FunderPortfolioDashboard onNavigate={handleNavClick} user={user} availableCount={availableCount} />}
      {activeTab === 'browse' && <BrowseTab selectedApplicationId={selectedApplicationId} onViewApplication={handleViewApplication} onBackToList={handleBackToList} funderSettings={funderSettings} funderUser={user} onNavigate={handleNavClick} applications={applications} refreshApplications={fetchApplications} />}
      {activeTab === 'my-leads' && <MyLeadsTab selectedApplicationId={selectedApplicationId} onViewApplication={handleViewApplication} onBackToList={handleBackToList} funderSettings={funderSettings} funderUser={user} onNavigate={handleNavClick} />}
      {activeTab === 'my-loans' && <MyLoansTab />}
      {activeTab === 'messages' && <MessagesInbox currentUser={user} />}
      {activeTab === 'settings' && <SettingsTab settings={funderSettings} onUpdate={fetchSettings} user={user} onUserUpdate={fetchUser} />}
      {activeTab === 'profile' && <ProfileTab user={user} onUpdate={fetchUser} />}
    </AuthenticatedLayout>
    </>
  );
};

/**
 * Hoisted out of FunderApplicationDetailView so React doesn't recreate the
 * component type on every render (react/no-unstable-nested-components).
 */
const InfoItem = ({ label, value }) => (
  <div className="mcl-form-group">
    <label className="mcl-form-label">{label}</label>
    <ReadOnlyValue value={value} />
  </div>
);

/* ================================================================
   Application Detail View with 3 Primary Actions
   ================================================================ */
export const FunderApplicationDetailView = ({ application, onBack, onInteract, onMakeOffer, onOfferChanged, funderUser }) => {
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const revokeOffer = async () => {
    setRevoking(true);
    try {
      await apiPost(`/offers/${application.offer_id}/withdraw`);
      toast.success('Offer revoked. You can make a new one when you are ready.');
      setConfirmRevoke(false);
      if (onOfferChanged) onOfferChanged();
    } catch (error) {
      toast.error(error.message || 'Failed to revoke offer');
    } finally {
      setRevoking(false);
    }
  };

  const [documentRequests, setDocumentRequests] = useState([]);
  const fetchDocumentRequests = async () => {
    try {
      const d = await apiGet(`/applications/${application.application_id}/document-requests`);
      setDocumentRequests(d.requests || []);
    } catch (e) { /* timeline degrades gracefully */ }
  };
  useEffect(() => { fetchDocumentRequests(); }, [application.application_id]);

  const getYesNoLabel = (value) => {
    const labels = { yes: 'Yes', no: 'No', not_sure: 'Not Sure', unknown: 'Unknown', expecting: 'Expecting' };
    return labels[value] || value || 'Not provided';
  };

  const formatStageOfCase = (stage) => {
    const labels = { pre_suit: 'Pre-suit', in_litigation: 'In Litigation', trial_scheduled: 'Trial Scheduled' };
    return labels[stage] || stage?.replace(/_/g, ' ') || '';
  };

  const isFavorited = application.funder_favorited;
  const isNotInterested = application.funder_not_interested;

  return (
    <div data-testid="application-detail-view">
      <ConfirmDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        tone="danger"
        title="Revoke this offer?"
        description="The plaintiff is comparing this offer right now. Revoking removes it from their comparison immediately."
        warning="You can make a new offer afterwards, but this one cannot be restored."
        confirmLabel="Revoke Offer"
        busyLabel="Revoking..."
        busy={revoking}
        onConfirm={revokeOffer}
        testId="confirm-revoke-offer"
      />
      <BackLink onClick={onBack} label="Back" />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 className="mcl-page-title">{application.firstName} {application.lastName}</h2>
          <p className="mcl-page-subtitle">{getCaseTypeLabel(application.caseType)} &middot; ID: {application.application_id}</p>
        </div>
        <StatusBadge status={application.status} />
      </div>

      {/* 3 Primary Actions Bar */}
      <div className="mcl-card" style={{ marginBottom: '1.5rem', borderColor: 'var(--mcl-primary)' }} data-testid="funder-actions-bar">
        <div className="mcl-card-body" style={{ padding: '1rem 1.25rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Hide all "Make/Resume/Another Offer" buttons once the application is closed
                (funded by another funder, cancelled, etc.). Backend enforces the same. */}
            {application.offer_status === 'accepted' ? (
              /* Checked before the closed branch: this funder won it, and
                 "no longer accepting offers" is a strange thing to tell the
                 winner. */
              <span data-testid="offer-accepted-notice" style={{ fontSize: '0.85rem', color: 'var(--mcl-success)', fontWeight: 600 }}>
                <CheckCircle size={15} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
                Your offer was accepted — this case is funded
              </span>
            ) : ['funded', 'closed', 'canceled', 'cancelled'].includes(application.status) ? (
              <span data-testid="application-closed-badge" style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)', fontWeight: 600 }}>
                {application.status === 'funded' ? 'Funded' : 'Closed'} — no longer accepting offers
              </span>
            ) : application.offer_status === 'draft' ? (
              <button className="mcl-btn mcl-btn-primary" onClick={() => onMakeOffer(application)} data-testid="resume-draft-btn">
                <DollarSign size={16} /> Resume Draft Offer
              </button>
            ) : application.offer_status === 'submitted' ? (
              <>
                <button className="mcl-btn mcl-btn-primary" onClick={() => onMakeOffer(application)} data-testid="edit-offer-btn">
                  <DollarSign size={16} /> View / Edit Offer
                </button>
                <button className="mcl-btn mcl-btn-outline" onClick={() => setConfirmRevoke(true)} data-testid="revoke-offer-btn">
                  <Undo2 size={16} /> Revoke Offer
                </button>
              </>
            ) : (
              /* No live offer. A withdrawn or declined one is history and
                 deliberately does not block a new bid. */
              <button className="mcl-btn mcl-btn-primary" onClick={() => onMakeOffer(application)} data-testid="make-offer-btn">
                <DollarSign size={16} /> Make Offer
              </button>
            )}
            {isNotInterested ? (
              <button className="mcl-btn mcl-btn-outline" onClick={() => onInteract(application.application_id, 'undo_not_interested')} data-testid="undo-not-interested-btn">
                <Undo2 size={16} /> Undo Not Interested
              </button>
            ) : (
              <button className="mcl-btn mcl-btn-outline" style={{ color: 'var(--mcl-text-muted)', borderColor: 'var(--mcl-border)' }} onClick={() => onInteract(application.application_id, 'not_interested')} data-testid="not-interested-btn">
                <ThumbsDown size={16} /> Not Interested
              </button>
            )}
            {isFavorited ? (
              <button className="mcl-btn mcl-btn-outline" style={{ borderColor: '#f59e0b', color: '#f59e0b' }} onClick={() => onInteract(application.application_id, 'undo_favorite')} data-testid="unfavorite-btn">
                <Star size={16} fill="#f59e0b" /> Saved
              </button>
            ) : (
              <button className="mcl-btn mcl-btn-outline" onClick={() => onInteract(application.application_id, 'favorite')} data-testid="favorite-btn">
                <Star size={16} /> Favorite
              </button>
            )}
            <InteractionBadge favorited={isFavorited} notInterested={isNotInterested} hasOffer={application.funder_has_offer} offerStatus={application.offer_status} />
            <button
              className="mcl-btn mcl-btn-outline"
              style={{ marginLeft: 'auto' }}
              onClick={async () => {
                try {
                  const blob = await apiBlob(`/applications/${application.application_id}/underwriting-pdf`);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `mycaseloan_application_${application.application_id}_underwriting.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success('PDF downloaded');
                } catch (e) {
                  console.error('PDF download error:', e);
                  toast.error('Failed to download PDF');
                }
              }}
              data-testid="download-underwriting-pdf-btn"
            >
              <FileText size={16} /> Download Underwriting PDF
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mcl-stats-row">
        <SummaryStatCard icon={DollarSign} label="Requested" value={application.requestedAmount ? `$${parseFloat(application.requestedAmount).toLocaleString()}` : '-'} variant="primary" />
        <SummaryStatCard icon={FileText} label="Case Type" value={getCaseTypeLabel(application.caseType)} variant="info" />
        <SummaryStatCard icon={CheckCircle} label="Validated" value={application.validated_at ? formatDate(application.validated_at) : 'Pending'} variant="success" />
      </div>

      {/* Lawyer Assessment */}
      {(application.internalNotes || application.liabilityStatus || application.stageOfCase || application.injuryTier) && (
        <div className="mcl-card" style={{ marginBottom: '1.25rem', borderColor: '#fde68a' }}>
          <div className="mcl-card-header" style={{ borderBottomColor: '#fde68a' }}>
            <h3 className="mcl-card-header-title" style={{ color: '#92400e' }}>Lawyer Assessment</h3>
          </div>
          <div className="mcl-card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: application.internalNotes ? '1rem' : 0 }}>
              {application.liabilityStatus && <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '8px' }}><div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#92400e', marginBottom: '0.25rem' }}>Liability</div><div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#78350f', textTransform: 'capitalize' }}>{application.liabilityStatus}</div></div>}
              {application.stageOfCase && <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '8px' }}><div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#92400e', marginBottom: '0.25rem' }}>Case Stage</div><div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#78350f' }}>{formatStageOfCase(application.stageOfCase)}</div></div>}
              {application.injuryTier && <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '8px' }}><div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#92400e', marginBottom: '0.25rem' }}>Injury Tier</div><div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#78350f', textTransform: 'capitalize' }}>{application.injuryTier}</div></div>}
              {(application.estimatedCaseValueMin || application.estimatedCaseValueMax) && <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '8px' }}><div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#92400e', marginBottom: '0.25rem' }}>Est. Case Value</div><div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#78350f' }}>{application.estimatedCaseValueMin && application.estimatedCaseValueMax ? `$${parseFloat(application.estimatedCaseValueMin).toLocaleString()} – $${parseFloat(application.estimatedCaseValueMax).toLocaleString()}` : application.estimatedCaseValueMin ? `$${parseFloat(application.estimatedCaseValueMin).toLocaleString()}+` : `Up to $${parseFloat(application.estimatedCaseValueMax).toLocaleString()}`}</div></div>}
              {application.lienPosition && <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '8px' }}><div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#92400e', marginBottom: '0.25rem' }}>Lien Position</div><div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#78350f' }}>{application.lienPosition}</div></div>}
            </div>
            {application.internalNotes && <div style={{ borderTop: '1px solid #fde68a', paddingTop: '0.75rem' }}><div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#92400e', marginBottom: '0.35rem' }}>Additional Notes</div><div style={{ fontSize: '0.875rem', color: '#92400e', whiteSpace: 'pre-wrap' }}>{application.internalNotes}</div></div>}
          </div>
        </div>
      )}

      {/* Client Information */}
      <SectionCard title="1. Client Information" className="mb-3">
        <div className="mcl-form-row">
          <InfoItem label="First Name" value={application.firstName} />
          <InfoItem label="Last Name" value={application.lastName} />
        </div>
        <InfoItem label="Address" value={[application.address1, application.address2, application.city, application.state, application.zip].filter(Boolean).join(', ')} />
        {/* The plaintiff's phone number is withheld from funders and the server
            no longer sends it (backend/application_privacy.py). The row is gone
            rather than blank: ReadOnlyValue renders "Not provided" for an empty
            value, which would tell a funder the plaintiff never gave one.
            lawFirmPhone below is a different field and stays — the attorney is
            the channel a funder is meant to use. */}
        <div className="mcl-form-row">
          <InfoItem label="Date of Birth" value={formatDateOnly(application.dateOfBirth)} />
          <InfoItem label="SSN (Last 4)" value={application.ssn ? `****${application.ssn}` : null} />
        </div>
      </SectionCard>

      {/* Sections 2 & 3 side by side */}
      <div className="mcl-sections-row">
      {/* Case Details */}
      <SectionCard title="2. Case Details" className="mb-3">
        <InfoItem label="Case Type" value={getCaseTypeLabel(application.caseType)} />
        <div className="mcl-form-row">
          <div className="mcl-form-row">
          <InfoItem label="Date of Incident" value={formatDateOnly(application.incidentDate)} />
          <InfoItem label="Requested Amount" value={application.requestedAmount ? `$${parseFloat(application.requestedAmount).toLocaleString()}` : null} />
          </div>
        </div>
        <div className="mcl-form-row">
          <InfoItem label="Accident State" value={application.accidentState} />
          <InfoItem label="Prior Funding" value={getYesNoLabel(application.hasPriorFunding)} />
          <InfoItem label="Offers / Settled" value={getYesNoLabel(application.hasOfferOrSettled)} />
        </div>
      </SectionCard>

      {/* Insurance Information */}
      <SectionCard title="3. Insurance Information" className="mb-3">
        <div className="mcl-form-row">
          <InfoItem label="Insurance Carrier" value={application.insuranceCarrier === 'other' ? application.insuranceCarrierOther : getInsuranceCarrierLabel(application.insuranceCarrier)} />
          <InfoItem label="Liability Policy Limits" value={getLiabilityLimitsLabel(application.liabilityLimits)} />
        </div>
        <InfoItem label="Uninsured Motorist (UM) Coverage" value={getYesNoLabel(application.hasUmCoverage)} />
        {application.hasUmCoverage === 'yes' && (
          <>
            <InfoItem label="UM Policy Limits" value={getLiabilityLimitsLabel(application.umLimits)} />
            <div className="mcl-form-row">
              <InfoItem label="UM Policy Number" value={application.umPolicyNumber} />
              <InfoItem label="UM Claim Number" value={application.umClaimNumber} />
            </div>
          </>
        )}
      </SectionCard>
      </div>{/* end mcl-sections-row */}

      {/* Law Firm */}
      <SectionCard title="4. Law Firm Information" className="mb-3">
        <div className="mcl-form-row">
          <InfoItem label="Lawyer Name" value={application.lawyerName} />
          <InfoItem label="Attorney Email" value={application.attorneyEmail} />
        </div>
        <div className="mcl-form-row">
          <InfoItem label="Law Firm" value={application.lawFirmName} />
          <InfoItem label="Law Firm Phone" value={application.lawFirmPhone} />
        </div>
      </SectionCard>

      {/* Injuries */}
      <SectionCard title="5. Injuries & Treatments" className="mb-3">
        <InfoItem label="Injuries Description" value={application.injuriesDescription} />
        <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <InfoItem label="Started Treatment" value={getYesNoLabel(application.hasStartedTreatment)} />
          <InfoItem label="PT / Chiro" value={getYesNoLabel(application.hadPtOrChiro)} />
          <InfoItem label="Serious Injections" value={getYesNoLabel(application.hadSeriousInjections)} />
        </div>
        {application.hasStartedTreatment === 'yes' && (
          <>
            <InfoItem label="Where did they go?" value={formatTreatmentLocations(application.treatmentLocations) || '-'} />
            <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <InfoItem label="Treatments Completed" value={application.treatmentsCompleted || '-'} />
              <InfoItem label="Surgical Recommendations" value={getYesNoLabel(application.surgicalRecommendations)} />
            </div>
          </>
        )}
        <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <InfoItem label="Broken Bones" value={getYesNoLabel(application.brokeBones)} />
          <InfoItem label="Surgery" value={getYesNoLabel(application.hadSurgery)} />
        </div>
        {application.otherInjuriesTreatments && <InfoItem label="Other" value={application.otherInjuriesTreatments} />}
      </SectionCard>

      {/* Additional Info */}
      <SectionCard title="6. Additional Information" className="mb-3">
        <div className="mcl-form-row">
          <InfoItem label="Child Support Liens" value={getYesNoLabel(application.childSupportLiens)} />
          <InfoItem label="In Bankruptcy" value={getYesNoLabel(application.inBankruptcy)} />
        </div>
      </SectionCard>

      {/* Activity Timeline — funder view (no offers since /offers is 403 for funders) */}
      <ActivityTimelineCard application={application} offers={[]} documentRequests={documentRequests} />
      {/* Documents (read-only for funders) */}
      <DocumentRequestsCard applicationId={application.application_id} mode="funder" getAuthHeaders={getAuthHeaders} onChanged={fetchDocumentRequests} />
      <SectionCard title="Messages" className="mb-3" data-testid="messages-card">
        <MessagesPanel applicationId={application.application_id} currentUser={funderUser} canStartThread />
      </SectionCard>

      {/* Once the loan is accepted, the funder can upload the executed final contract. */}
      {application.status === 'funded' && application.offer_status === 'accepted' && (
        <FunderFinalContractCard
          applicationId={application.application_id}
          getAuthHeaders={getAuthHeaders}
        />
      )}
      <DocumentUpload applicationId={application.application_id} canUpload={false} getAuthHeaders={getAuthHeaders} userRole="funder" />
    </div>
  );
};

/* ================================================================
   Browse Tab — Open marketplace, no access gating
   ================================================================ */
export const BrowseTab = ({ selectedApplicationId, onViewApplication, onBackToList, funderSettings, funderUser, onNavigate, applications, refreshApplications }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showOfferBuilder, setShowOfferBuilder] = useState(false);
  const [offerBuilderApp, setOfferBuilderApp] = useState(null);
  const [filterMode, setFilterMode] = useState('all'); // all, not_viewed, favorited

  useEffect(() => {
    if (selectedApplicationId) {
      loadApplicationDetail(selectedApplicationId);
    } else {
      setSelectedApplication(null);
    }
  }, [selectedApplicationId]);

  const loadApplicationDetail = async (appId) => {
    setLoadingDetails(true);
    try {
      const data = await apiGet(`/applications/${appId}`);
      // Merge funder state from browse list
      const browseApp = applications.find(a => a.application_id === appId);
      if (browseApp) { data.funder_favorited = browseApp.funder_favorited; data.funder_not_interested = browseApp.funder_not_interested; data.funder_has_offer = browseApp.funder_has_offer; data.offer_status = browseApp.offer_status; data.offer_id = browseApp.offer_id; }
      setSelectedApplication(data);
    } catch (error) { toast.error(error.message || 'Failed to load details'); }
    finally { setLoadingDetails(false); }
  };

  const handleInteract = async (appId, action) => {
    try {
      await apiPost('/funder/interactions', { application_id: appId, action });
      toast.success(action === 'favorite' ? 'Added to favorites' : action === 'not_interested' ? 'Marked as not interested' : action === 'undo_favorite' ? 'Removed from favorites' : 'Removed not interested');
      refreshApplications();
      if (selectedApplication && selectedApplication.application_id === appId) {
        setSelectedApplication(prev => ({
          ...prev,
          funder_favorited: action === 'favorite' ? true : action === 'undo_favorite' ? false : (action === 'not_interested' ? false : prev.funder_favorited),
          funder_not_interested: action === 'not_interested' ? true : action === 'undo_not_interested' ? false : (action === 'favorite' ? false : prev.funder_not_interested),
        }));
      }
    } catch (error) { toast.error(error.message || 'Failed'); }
  };

  const handleMakeOffer = (app) => { setOfferBuilderApp(app); setShowOfferBuilder(true); };

  const formatStageOfCase = (stage) => {
    const labels = { pre_suit: 'Pre-suit', in_litigation: 'In Litigation', trial_scheduled: 'Trial Scheduled' };
    return labels[stage] || stage?.replace(/_/g, ' ');
  };

  // The Settings-driven half of the filter, pulled out so the indicator can say
  // how many applications it is responsible for hiding. A funder whose preferred
  // states match nothing on the marketplace sees an empty list with no idea why;
  // "the server returned nothing" and "your own settings excluded everything"
  // looked identical before this. The rule itself lives in lib/funderAvailability
  // because the sidebar badge and the dashboard banner must apply the same one.
  const passesSettingsFilters = (app) => passesFunderSettings(app, funderSettings);

  const hiddenBySettings = applications.filter(app => !passesSettingsFilters(app)).length;

  const settingsFilterSummary = [
    funderSettings.preferred_states?.length > 0 && `states ${funderSettings.preferred_states.join(', ')}`,
    funderSettings.max_loan_amount && `advances up to $${Number(funderSettings.max_loan_amount).toLocaleString('en-US')}`,
  ].filter(Boolean).join(' · ');

  const matchesSearch = (app) =>
    (app.caseType || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.lastName || '').toLowerCase().includes(searchTerm.toLowerCase());

  // The promoted strip follows the search too. A strip advertising Sam Cole
  // above a table filtered to Priya Nair reads as a bug.
  const winnable = applications.filter(app => matchesSearch(app) && isWinnable(app, funderSettings));

  const filteredApplications = applications.filter(app => {
    if (!matchesSearch(app)) return false;
    if (!passesSettingsFilters(app)) return false;
    if (filterMode === 'favorited') return app.funder_favorited;
    if (filterMode === 'not_viewed') return !app.funder_favorited && !app.funder_not_interested && !app.funder_has_offer;
    return !app.funder_not_interested; // 'all' hides not-interested by default
  });

  if (showOfferBuilder && offerBuilderApp) {
    return <FunderOfferBuilder application={offerBuilderApp} onClose={() => { setShowOfferBuilder(false); setOfferBuilderApp(null); }} onOfferCreated={() => { refreshApplications(); setShowOfferBuilder(false); setOfferBuilderApp(null); }} onNavigate={onNavigate} getAuthHeaders={getAuthHeaders} funderSettings={funderSettings} funderUser={funderUser} />;
  }

  if (selectedApplication) {
    return <FunderApplicationDetailView application={selectedApplication} onBack={() => onBackToList ? onBackToList() : setSelectedApplication(null)} onInteract={handleInteract} onMakeOffer={handleMakeOffer} onOfferChanged={() => { refreshApplications(); loadApplicationDetail(selectedApplication.application_id); }} funderUser={funderUser} />;
  }

  return (
    <div data-testid="browse-tab">
      <PageHeader title="Browse Applications" subtitle="View all lawyer-approved funding opportunities. Click into any application to review full details." />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ flex: '1 1 300px' }}>
          <FilterToolbar searchValue={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search by case type or client name..." />
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          {['all', 'not_viewed', 'favorited'].map((f) => (
            <button key={f} className={`mcl-btn mcl-btn-sm ${filterMode === f ? 'mcl-btn-primary' : 'mcl-btn-ghost'}`} onClick={() => setFilterMode(f)} data-testid={`filter-${f}`}>
              {f === 'all' ? 'All' : f === 'not_viewed' ? 'New' : 'Favorited'}
            </button>
          ))}
          {settingsFilterSummary && (
            // A button, not a span: these filters are the most common reason the
            // list looks empty, and the only way to change them is the Settings
            // tab. .mcl-btn also gives it the inline-flex + nowrap its siblings
            // have — as a bare span the gear icon wrapped onto its own line.
            <button
              type="button"
              className="mcl-btn mcl-btn-sm mcl-btn-ghost"
              onClick={() => onNavigate?.('settings')}
              title={`${settingsFilterSummary}. Click to change them in Settings.`}
              aria-label={`Your settings are filtering this list: ${settingsFilterSummary}. Go to Settings to change them.`}
              data-testid="settings-filter-indicator"
              style={{ color: 'var(--mcl-primary)', fontWeight: 600 }}
            >
              <Settings size={13} /> Filtered
              {hiddenBySettings > 0 && <span style={{ fontWeight: 500, opacity: 0.85 }}>({hiddenBySettings} hidden)</span>}
            </button>
          )}
        </div>
      </div>

      {winnable.length > 0 && (
        <div data-testid="winnable-strip" style={{ marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.85rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--mcl-text-heading)', margin: 0 }}>Still winnable</h2>
            <span style={{ fontSize: '0.8125rem', color: 'var(--mcl-text-muted)' }}>Others have bid, none accepted, and you have not</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
            {winnable.map((app) => (
              <WinnableCard
                key={app.application_id}
                application={app}
                onView={(id) => onViewApplication ? onViewApplication(id) : loadApplicationDetail(id)}
                onMakeOffer={handleMakeOffer}
              />
            ))}
          </div>
        </div>
      )}

      <SectionCard title={`Applications (${filteredApplications.length})`}>
        {filteredApplications.length === 0 ? (
          // "The marketplace is empty" and "your own settings excluded every
          // application" used to render identically. When the settings are the
          // reason, say so and offer the way out.
          hiddenBySettings > 0 && hiddenBySettings === applications.length ? (
            <EmptyState
              icon={Settings}
              title="Your settings are hiding every application"
              text={`All ${applications.length} available application${applications.length === 1 ? '' : 's'} fall outside your filters (${settingsFilterSummary}).`}
              actions={(
                <button className="mcl-btn mcl-btn-primary mcl-btn-sm" onClick={() => onNavigate?.('settings')} data-testid="empty-state-open-settings">
                  <Settings size={14} /> Adjust filters in Settings
                </button>
              )}
            />
          ) : (
            <EmptyState icon={Search} title="No applications found" text="No matching applications at this time." />
          )
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table">
              <thead><tr><th>Client</th><th>Case Type</th><th>Amount</th><th>Location</th><th>Offer Activity</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredApplications.map((app) => (
                  <tr key={app.application_id} data-testid={`application-${app.application_id}`}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>
                        {app.firstName} {app.lastName}
                        {app.funder_favorited && <Star size={12} fill="#f59e0b" style={{ color: '#f59e0b', marginLeft: '0.35rem', verticalAlign: '-1px' }} />}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>
                        {app.injuryTier ? <span style={{ textTransform: 'capitalize' }}>{app.injuryTier} tier</span> : null}
                        {app.stageOfCase ? <> &middot; {formatStageOfCase(app.stageOfCase)}</> : null}
                      </div>
                    </td>
                    <td>{getCaseTypeLabel(app.caseType)}</td>
                    <td style={{ fontWeight: 600 }}>{app.requestedAmount ? `$${parseFloat(app.requestedAmount).toLocaleString()}` : '-'}</td>
                    <td>{app.accidentState || app.state || '-'}</td>
                    <td><OfferActivity application={app} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                        <button className="mcl-action-btn primary" onClick={() => onViewApplication ? onViewApplication(app.application_id) : loadApplicationDetail(app.application_id)} disabled={loadingDetails} data-testid={`view-details-button-${app.application_id}`}><Eye size={14} /> View</button>
                        {/* Bidding used to cost a screen: the row offered only View.
                            This button also carries the funder's own offer state, which
                            is why no badge repeats it in the Offer Activity column.
                            Withdrawn and declined offers are history — they read as
                            "Make an offer", because the funder may bid again.

                            It precedes the favourite star deliberately: the star only
                            renders on rows that are neither favourited nor marked
                            not-interested, so behind it the primary action landed in a
                            different place per row and the column read ragged. */}
                        {app.offer_status === 'draft' ? (
                          <button className="mcl-btn mcl-btn-sm mcl-btn-outline-primary" onClick={() => handleMakeOffer(app)} data-testid={`resume-offer-${app.application_id}`}><DollarSign size={14} /> Resume draft</button>
                        ) : hasLiveOffer(app) ? (
                          <button className="mcl-btn mcl-btn-sm mcl-btn-outline-primary" onClick={() => handleMakeOffer(app)} data-testid={`update-offer-${app.application_id}`}><DollarSign size={14} /> Update offer</button>
                        ) : (
                          <button className="mcl-btn mcl-btn-sm mcl-btn-primary" onClick={() => handleMakeOffer(app)} data-testid={`make-offer-${app.application_id}`}><DollarSign size={14} /> Make an offer</button>
                        )}
                        {!app.funder_favorited && !app.funder_not_interested && (
                          <button className="mcl-action-btn" style={{ color: '#f59e0b' }} onClick={() => handleInteract(app.application_id, 'favorite')} data-testid={`quick-fav-${app.application_id}`} title="Favorite"><Star size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

/* ================================================================
   My Leads Tab — Favorited + Offers Submitted
   ================================================================ */
const MyLeadsTab = ({ selectedApplicationId, onViewApplication, onBackToList, funderSettings, funderUser, onNavigate }) => {
  const [leads, setLeads] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showOfferBuilder, setShowOfferBuilder] = useState(false);
  const [offerBuilderApp, setOfferBuilderApp] = useState(null);
  const [filterMode, setFilterMode] = useState('all');

  useEffect(() => { fetchLeads(); }, []);

  useEffect(() => {
    if (selectedApplicationId) {
      loadDetail(selectedApplicationId);
    } else { setSelectedApplication(null); }
  }, [selectedApplicationId]);

  const fetchLeads = async () => {
    try { setLeads(await apiGet('/funder/leads')); } catch (error) { console.error('Failed:', error); }
  };

  const loadDetail = async (appId) => {
    setLoadingDetails(true);
    try {
      const data = await apiGet(`/applications/${appId}`);
      const lead = leads.find(l => l.application_id === appId);
      if (lead) { data.funder_favorited = lead.funder_favorited; data.funder_not_interested = lead.funder_not_interested; data.funder_has_offer = lead.funder_has_offer; data.offer_status = lead.offer_status; }
      setSelectedApplication(data);
    } catch (error) { toast.error(error.message || 'Failed to load'); }
    finally { setLoadingDetails(false); }
  };

  const handleInteract = async (appId, action) => {
    try {
      await apiPost('/funder/interactions', { application_id: appId, action });
      toast.success('Updated'); fetchLeads(); if (selectedApplication?.application_id === appId) loadDetail(appId);
    } catch (error) { toast.error(error.message || 'Failed'); }
  };

  const handleMakeOffer = (app) => { setOfferBuilderApp(app); setShowOfferBuilder(true); };

  const filtered = leads.filter(app => {
    if (filterMode === 'drafts') return app.offer_status === 'draft';
    if (filterMode === 'favorites') return app.funder_favorited;
    if (filterMode === 'offers') return app.funder_has_offer && app.offer_status !== 'draft';
    if (filterMode === 'not_interested') return app.funder_not_interested;
    return true;
  });

  if (showOfferBuilder && offerBuilderApp) {
    return <FunderOfferBuilder application={offerBuilderApp} onClose={() => { setShowOfferBuilder(false); setOfferBuilderApp(null); }} onOfferCreated={() => { fetchLeads(); setShowOfferBuilder(false); setOfferBuilderApp(null); }} onNavigate={onNavigate} getAuthHeaders={getAuthHeaders} funderSettings={funderSettings} funderUser={funderUser} />;
  }

  if (selectedApplication) {
    return <FunderApplicationDetailView application={selectedApplication} onBack={() => onBackToList ? onBackToList() : setSelectedApplication(null)} onInteract={handleInteract} onMakeOffer={handleMakeOffer} onOfferChanged={() => { fetchLeads(); loadDetail(selectedApplication.application_id); }} funderUser={funderUser} />;
  }

  return (
    <div data-testid="my-leads-tab">
      <PageHeader title="My Leads" subtitle="Applications you've favorited or submitted offers on." />

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'drafts', label: 'Drafts' },
          { key: 'favorites', label: 'Favorites' },
          { key: 'offers', label: 'Offers Submitted' },
          { key: 'not_interested', label: 'Not Interested' },
        ].map((f) => (
          <button key={f.key} className={`mcl-btn mcl-btn-sm ${filterMode === f.key ? 'mcl-btn-primary' : 'mcl-btn-ghost'}`} onClick={() => setFilterMode(f.key)} data-testid={`leads-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      <SectionCard title={`Leads (${filtered.length})`}>
        {filtered.length === 0 ? (
          <EmptyState icon={Star} title="No leads yet" text="Browse applications and favorite or submit offers to build your lead pipeline." />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table">
              <thead><tr><th>Client</th><th>Case Type</th><th>Amount</th><th>Law Firm</th><th>Pipeline Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map((app) => (
                  <tr key={app.application_id} data-testid={`lead-${app.application_id}`}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{app.firstName} {app.lastName}</div>
                      {app.city && app.state && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{app.city}, {app.state}</div>}
                    </td>
                    <td>{getCaseTypeLabel(app.caseType)}</td>
                    <td style={{ fontWeight: 600 }}>{app.requestedAmount ? `$${parseFloat(app.requestedAmount).toLocaleString()}` : '-'}</td>
                    <td>{app.lawFirmName || '-'}</td>
                    <td><InteractionBadge favorited={app.funder_favorited} notInterested={app.funder_not_interested} hasOffer={app.funder_has_offer} offerStatus={app.offer_status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="mcl-action-btn primary" onClick={() => onViewApplication ? onViewApplication(app.application_id) : loadDetail(app.application_id)} disabled={loadingDetails} data-testid={`view-lead-${app.application_id}`}><Eye size={14} /> View</button>
                        {!app.funder_has_offer && <button className="mcl-btn mcl-btn-sm mcl-btn-primary" onClick={() => handleMakeOffer(app)} data-testid={`offer-lead-${app.application_id}`}><DollarSign size={14} /> Offer</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};


/* ================================================================
   My Loans Tab
   ================================================================ */
const MyLoansTab = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLoan, setExpandedLoan] = useState(null);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      setLoans(await apiGet('/funder/loans'));
    } catch (e) { console.error('Failed to fetch loans:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLoans(); }, []);

  const fmt = (v) => v ? parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--mcl-text-muted)' }}>Loading loans...</div>;

  return (
    <div data-testid="my-loans-tab">
      <PageHeader title="My Loans" subtitle="Active funded loans where your offer has been accepted." />

      <SectionCard title={`Active Loans (${loans.length})`}>
        {loans.length === 0 ? (
          <EmptyState icon={Briefcase} title="No active loans" text="When a client accepts one of your submitted offers, it will appear here as an active loan." />
        ) : (
          <div>
            {loans.map((loan, idx) => {
              const isExpanded = expandedLoan === idx;
              // v1 offers are frozen and keep their dated schedule forever;
              // an absent pricing_version means v1, never v2.
              const isV2 = loan.pricing_version === PRICING_VERSION;
              const bands = isV2 ? loan.repayment_bands : null;
              return (
              <div key={loan.offer_id || idx} data-testid={`loan-${idx}`} className="mcl-card" style={{ marginBottom: '1rem', borderColor: '#a7f3d0' }}>
                <div className="mcl-card-body" style={{ padding: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                    {/* Left: Client Info */}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--mcl-text-heading)', marginBottom: '4px' }}>{loan.client_name || 'Client'}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)', marginBottom: '2px' }}>{getCaseTypeLabel(loan.case_type)}</div>
                      {loan.location && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}><MapPin size={11} style={{ verticalAlign: 'middle', marginRight: '3px' }} />{loan.location}</div>}
                      {loan.law_firm && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)', marginTop: '2px' }}>Firm: {loan.law_firm}</div>}
                    </div>

                    {/* Right: Key Financials */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: '0.75rem 1.5rem', textAlign: 'right' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Advance</div>
                        <div style={{ fontWeight: 700, color: '#065f46', fontSize: '1.1rem' }}>${fmt(loan.advance_amount)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Obligation</div>
                        <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>${fmt(loan.total_funded_amount)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Max Cap</div>
                        <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>${fmt(loan.maximum_cap)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rate</div>
                        <div style={{ fontWeight: 600 }}>{loan.annualized_rate}%</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Term</div>
                        <div style={{ fontWeight: 600 }}>{loan.max_term_months || '-'}mo</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Accepted</div>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{loan.accepted_at ? formatDate(loan.accepted_at) : '-'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Schedule */}
                  <div style={{ borderTop: '1px solid var(--mcl-border)', marginTop: '1rem', paddingTop: '0.75rem' }}>
                    <button
                      onClick={() => setExpandedLoan(expandedLoan === idx ? null : idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mcl-primary)', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px', padding: 0 }}
                      data-testid={`toggle-loan-schedule-${idx}`}
                    >
                      {expandedLoan === idx ? 'Hide Repayment Schedule' : 'View Repayment Schedule'}
                    </button>

                    {isExpanded && isV2 ? (
                      bands?.length > 0 && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <table className="mcl-table" style={{ fontSize: '0.8125rem' }} data-testid="repayment-schedule-table">
                            <thead>
                              <tr>
                                <th>If Your Case Settles</th>
                                <th style={{ textAlign: 'right' }}>Total Repayment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bands.map((band, i) => (
                                <tr key={i} data-testid={`band-row-${i}`} style={band.is_cap ? { backgroundColor: 'var(--mcl-warning-bg)' } : {}}>
                                  <td>
                                    <div data-testid="band-period" style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>
                                      {formatBandPeriodLabel(band.from_month, band.to_month)}
                                    </div>
                                    <div data-testid="band-dates" style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)', marginTop: '0.1rem' }}>
                                      {formatBandDateRangeLabel(band.from, band.to)}
                                      {band.is_cap && <span style={{ marginLeft: '0.4rem', color: 'var(--mcl-warning)', fontWeight: 600 }}>(Final Cap)</span>}
                                    </div>
                                  </td>
                                  <td data-testid="band-amount" style={{ textAlign: 'right', fontWeight: 600 }}>${fmt(band.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : (
                      isExpanded && !isV2 && loan.repayment_schedule?.length > 0 && (
                        <div style={{ marginTop: '0.75rem' }}>
                          <table className="mcl-table" style={{ fontSize: '0.8125rem' }}>
                            <thead>
                              <tr>
                                <th>If Paid On or Before</th>
                                <th style={{ textAlign: 'right' }}>Total Repayment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* The v1 palette, verbatim. These two hex values predate the
                                  redesign and are deliberately NOT tokenised: CLAUDE.md rules
                                  the authenticated app off the --mcl-* palette, and the token
                                  pair resolves to different colours (#fffbeb / #d97706), which
                                  would restyle every existing v1 loan. */}
                              {loan.repayment_schedule.map((row, i) => (
                                <tr key={`${row.date}-${i}`} data-testid={`v1-schedule-row-${i}`} style={row.is_cap ? { backgroundColor: '#fef9c3' } : {}}>
                                  <td>{row.date}{row.is_cap && <span data-testid="v1-schedule-cap-label" style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: '#854d0e', fontWeight: 600 }}>(Final Cap)</span>}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>${fmt(row.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
};


/* ================================================================
   Settings Tab
   ================================================================ */
const TERM_OPTIONS = [6, 12, 18, 24, 30, 36, 42, 48, 54, 60];

const SettingsTab = ({ settings, onUpdate, user, onUserUpdate }) => {
  const [form, setForm] = useState({
    max_loan_amount: settings.max_loan_amount ?? '',
    max_cap_rate: settings.max_cap_rate ?? 3.0,
    max_annualized_rate: settings.max_annualized_rate ?? 28,
    max_term_months: settings.max_term_months ?? 36,
    preferred_states: settings.preferred_states ?? [],
  });
  const [saving, setSaving] = useState(false);

  // Fee editor (separate save — hits PUT /users/me).
  //
  // `??` and an explicit `!== false`, never `||`: a funder may legitimately
  // set a fee of 0 or turn accrual off, and `||` would silently restore the
  // platform default over either choice.
  const feeFormFromUser = (u) => ({
    broker_fee_percentage: u?.broker_fee_percentage ?? 15,
    broker_fee_cap: u?.broker_fee_cap ?? '',
    application_fee: u?.application_fee ?? 99,
    processing_fee: u?.processing_fee ?? DEFAULT_PROCESSING_FEE,
    default_accrual_interval_months: u?.default_accrual_interval_months ?? DEFAULT_INTERVAL,
    default_fees_accrue_interest: u?.default_fees_accrue_interest !== false,
    default_fee_model: u?.default_fee_model ?? DEFAULT_FEE_MODEL,
  });
  const [feeForm, setFeeForm] = useState(() => feeFormFromUser(user));
  const [savingFees, setSavingFees] = useState(false);

  useEffect(() => {
    setFeeForm(feeFormFromUser(user));
  }, [user]);

  const handleSaveFees = async (e) => {
    e.preventDefault();
    setSavingFees(true);
    try {
      const payload = {
        broker_fee_percentage: parseFloat(feeForm.broker_fee_percentage),
        broker_fee_cap: feeForm.broker_fee_cap === '' || feeForm.broker_fee_cap === null ? null : parseFloat(feeForm.broker_fee_cap),
        application_fee: parseFloat(feeForm.application_fee),
        // Empty clears the override back to the platform default; 0 is a real
        // value (waived) and must survive, so the emptiness test is explicit
        // rather than falsiness.
        processing_fee: feeForm.processing_fee === '' || feeForm.processing_fee === null ? null : parseFloat(feeForm.processing_fee),
        default_accrual_interval_months: parseInt(feeForm.default_accrual_interval_months, 10),
        // A deliberate `false` has to reach the wire — PUT /users/me keeps
        // this field through its null filter for exactly that reason.
        default_fees_accrue_interest: !!feeForm.default_fees_accrue_interest,
        default_fee_model: feeForm.default_fee_model,
      };
      await apiPut('/users/me', payload);
      toast.success('Fees updated'); onUserUpdate && onUserUpdate();
    } catch (error) { toast.error(error.message || 'Failed to save fees'); }
    finally { setSavingFees(false); }
  };

  useEffect(() => {
    setForm({
      max_loan_amount: settings.max_loan_amount ?? '',
      max_cap_rate: settings.max_cap_rate ?? 3.0,
      max_annualized_rate: settings.max_annualized_rate ?? 28,
      max_term_months: settings.max_term_months ?? 36,
      preferred_states: settings.preferred_states ?? [],
    });
  }, [settings]);

  const toggleState = (abbr) => {
    setForm(prev => ({
      ...prev,
      preferred_states: prev.preferred_states.includes(abbr)
        ? prev.preferred_states.filter(s => s !== abbr)
        : [...prev.preferred_states, abbr]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        max_loan_amount: form.max_loan_amount === '' || form.max_loan_amount === null ? null : parseFloat(form.max_loan_amount),
        max_cap_rate: parseFloat(form.max_cap_rate),
        max_annualized_rate: parseFloat(form.max_annualized_rate),
        max_term_months: parseInt(form.max_term_months),
        preferred_states: form.preferred_states,
      };
      await apiPut('/funder/settings', payload);
      toast.success('Settings saved'); onUpdate();
    } catch (error) { toast.error(error.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-tab">
      <PageHeader title="Funder Settings" subtitle="Configure your default search criteria and offer parameters. These settings filter your Browse view and pre-fill the offer builder." />
      <form onSubmit={handleSave}>

        <SectionCard title="Loan Parameters" className="mb-4">
          <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Max Loan Amount</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600 }}>$</span>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="0"
                  step="1000"
                  placeholder="Any (no limit)"
                  value={form.max_loan_amount}
                  onChange={(e) => setForm({ ...form, max_loan_amount: e.target.value })}
                  style={{ paddingLeft: '28px' }}
                  data-testid="setting-max-loan-amount"
                />
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Leave empty for no limit</span>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Max Term (months)</label>
              <select
                className="mcl-form-input"
                value={form.max_term_months}
                onChange={(e) => setForm({ ...form, max_term_months: e.target.value })}
                data-testid="setting-max-term"
              >
                {TERM_OPTIONS.map(m => <option key={m} value={m}>{m} months</option>)}
              </select>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Rate Limits" className="mb-4">
          <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Max Cap Rate</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={form.max_cap_rate}
                  onChange={(e) => setForm({ ...form, max_cap_rate: e.target.value })}
                  data-testid="setting-max-cap-rate"
                />
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600, fontSize: '0.85rem' }}>x</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Maximum multiplier on the advance (default 3x)</span>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Max Annualized Rate</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="1"
                  max="100"
                  step="0.5"
                  value={form.max_annualized_rate}
                  onChange={(e) => setForm({ ...form, max_annualized_rate: e.target.value })}
                  data-testid="setting-max-annualized-rate"
                />
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600, fontSize: '0.85rem' }}>%</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Maximum annualized percentage rate (default 28%)</span>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Preferred States" className="mb-4">
          <p style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)', marginBottom: '1rem' }}>
            Select the states you want to fund in. Leave all unselected to see applications from any state.
            <span style={{ fontWeight: 600, color: 'var(--mcl-primary)', marginLeft: '0.5rem' }}>
              {form.preferred_states.length > 0 ? `${form.preferred_states.length} selected` : 'All states'}
            </span>
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }} data-testid="setting-preferred-states">
            {US_STATES.map(s => {
              const active = form.preferred_states.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleState(s.value)}
                  className={`mcl-btn mcl-btn-sm ${active ? 'mcl-btn-primary' : 'mcl-btn-ghost'}`}
                  style={{ minWidth: '52px', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }}
                  data-testid={`state-${s.value}`}
                >
                  {s.value}
                </button>
              );
            })}
          </div>
          {form.preferred_states.length > 0 && (
            <button type="button" className="mcl-btn mcl-btn-sm mcl-btn-ghost" style={{ marginTop: '0.75rem', color: 'var(--mcl-text-muted)' }} onClick={() => setForm({ ...form, preferred_states: [] })} data-testid="clear-states-btn">
              Clear all states
            </button>
          )}
        </SectionCard>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="submit" className="mcl-btn mcl-btn-primary" disabled={saving} data-testid="save-settings-btn">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* My Fees — separate form because it hits PUT /users/me, not /funder/settings */}
      <form onSubmit={handleSaveFees} style={{ marginTop: '1.5rem' }} data-testid="funder-fees-form">
        <SectionCard
          title="My Fees"
          description="Your standing terms. Every offer you build starts from these; changing one for a single offer never writes back here."
        >
          <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Broker Fee (%)</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={feeForm.broker_fee_percentage}
                  onChange={(e) => setFeeForm({ ...feeForm, broker_fee_percentage: e.target.value })}
                  data-testid="setting-broker-fee-pct"
                />
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600, fontSize: '0.85rem' }}>%</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>0 = waived</span>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Broker Fee Cap</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600 }}>$</span>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="0"
                  step="50"
                  placeholder="No cap"
                  value={feeForm.broker_fee_cap ?? ''}
                  onChange={(e) => setFeeForm({ ...feeForm, broker_fee_cap: e.target.value })}
                  style={{ paddingLeft: '28px' }}
                  data-testid="setting-broker-fee-cap"
                />
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Leave empty for no cap</span>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Application Fee</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600 }}>$</span>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="0"
                  step="1"
                  value={feeForm.application_fee}
                  onChange={(e) => setFeeForm({ ...feeForm, application_fee: e.target.value })}
                  style={{ paddingLeft: '28px' }}
                  data-testid="setting-application-fee"
                />
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Default $99 · 0 = waived</span>
            </div>
          </div>

          {/* The pricing defaults the offer builder seeds from. Same card as
              the three fees above because they answer the same question —
              what do I charge as a matter of course — and the builder reads
              all seven from one place. */}
          <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Fee Model</label>
              <select
                className="mcl-form-input"
                value={feeForm.default_fee_model}
                onChange={(e) => setFeeForm({ ...feeForm, default_fee_model: e.target.value })}
                data-testid="setting-fee-model"
              >
                {VALID_FEE_MODELS.map((m) => (
                  <option key={m} value={m}>{m === 'itemized' ? 'Itemized (broker + application)' : 'Flat processing fee'}</option>
                ))}
              </select>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Which fees you charge by default</span>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Processing Fee</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 600 }}>$</span>
                <input
                  className="mcl-form-input"
                  type="number"
                  min="0"
                  step="1"
                  value={feeForm.processing_fee}
                  onChange={(e) => setFeeForm({ ...feeForm, processing_fee: e.target.value })}
                  style={{ paddingLeft: '28px' }}
                  data-testid="setting-processing-fee"
                />
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Used when the fee model is flat · 0 = waived</span>
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Accrual Interval</label>
              <select
                className="mcl-form-input"
                value={feeForm.default_accrual_interval_months}
                onChange={(e) => setFeeForm({ ...feeForm, default_accrual_interval_months: e.target.value })}
                data-testid="setting-accrual-interval"
              >
                {VALID_INTERVALS.map((m) => (
                  <option key={m} value={m}>{m === 1 ? 'Monthly' : `${m} months`}</option>
                ))}
              </select>
              <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>How wide each payment window is</span>
            </div>
          </div>

          <div className="mcl-form-group" data-testid="setting-fees-accrue-group">
            <label className="mcl-checkbox-label">
              <input
                type="checkbox"
                checked={feeForm.default_fees_accrue_interest}
                onChange={(e) => setFeeForm({ ...feeForm, default_fees_accrue_interest: e.target.checked })}
                data-testid="setting-fees-accrue"
              />
              {' '}Fees accrue interest
            </label>
            <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)', display: 'block' }}>
              {feeForm.default_fees_accrue_interest
                ? 'Interest runs on the advance plus all fees'
                : 'Fees ride on top, uncompounded'}
            </span>
          </div>

          <button type="submit" className="mcl-btn mcl-btn-primary" disabled={savingFees} data-testid="save-fees-btn" style={{ marginTop: '0.5rem' }}>
            {savingFees ? 'Saving...' : 'Save Fees'}
          </button>
        </SectionCard>
      </form>

      {/* Notification preferences — own form so toggling doesn't clobber the
          fee editor. */}
      <NotificationPreferences user={user} onUserUpdate={onUserUpdate} />

      {/* Lawyer Lead Subscriptions — outside the settings form so its own
          state and POST/DELETE actions don't trigger the parent save. */}
      <div style={{ marginTop: '2rem' }}>
        <LawyerSubscriptions getAuthHeaders={getAuthHeaders} />
      </div>
    </div>
  );
};

const NotificationPreferences = ({ user, onUserUpdate }) => {
  // Default subscribed unless the user has explicitly opted out.
  const initialOptIn = user?.notify_on_new_leads !== false;
  const [optIn, setOptIn] = useState(initialOptIn);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOptIn(user?.notify_on_new_leads !== false);
  }, [user]);

  const handleToggle = async (next) => {
    setSaving(true);
    setOptIn(next);
    try {
      await apiPut('/users/me', { notify_on_new_leads: next });
      toast.success(next ? 'New-lead alerts on' : 'New-lead alerts off');
      onUserUpdate && onUserUpdate();
    } catch (error) {
      setOptIn(!next);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Notifications" className="mb-4" style={{ marginTop: '2rem' }}>
      <label
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', cursor: 'pointer' }}
        data-testid="notify-new-leads-toggle-label"
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>New Lead Available</div>
          <div style={{ fontSize: '0.83rem', color: 'var(--mcl-text-muted)', marginTop: 2 }}>
            Email me when an attorney approves a new application. You can turn this off any time.
          </div>
        </div>
        <input
          type="checkbox"
          checked={optIn}
          disabled={saving}
          onChange={(e) => handleToggle(e.target.checked)}
          data-testid="notify-new-leads-toggle"
          style={{ width: 20, height: 20, cursor: 'pointer' }}
        />
      </label>
    </SectionCard>
  );
};

export default FunderDashboard;
